import { z } from 'zod';
import { chargerQrUrl } from './lib/chargerQr.js';
export const PO_STAGES = ['drafted', 'quote_requested', 'quote_received', 'po_sent', 'po_acknowledged', 'precommission_submitted', 'sims_shipped', 'invoiced', 'paid', 'shipped', 'delivered', 'pushed_online'];
export const PO_OCPP_BASE_URL = 'wss://ocpp.evinitiative.com/';
export const PO_PREFLIP_NOTE = 'Units will connect to v3 production; drivers see them after the flip.';
export const PO_CONTACTS = [
    { name: 'Min Wan', purpose: 'Orders', email: 'mwan@autel.com' },
    { name: 'Autel orders', purpose: 'Orders', email: 'evorder@autel.com' },
    { name: 'Logistics', purpose: 'Freight', email: 'Logistics.NC@autel.com' },
    { name: 'Anya Huang', purpose: 'Shipment', email: '' },
    { name: 'Owen Q', purpose: 'Invoices', email: '' },
    { name: 'Nav Sandhu', purpose: 'Pushes and support', email: '' },
    { name: 'Commercial EV support', purpose: 'Pushes and support', email: 'commercialEVSupport@autel.com' },
];
export const procurementText = (max) => z.string().trim().max(max).regex(/^(?![\s\S]*(?:(?:password|auth code|pin)\s*[:=]|(?:sk_live|sk_test|whsec)_[a-z0-9]+))[\s\S]*$/i, 'Do not include passwords or credentials in procurement records.');
const text = procurementText;
const id = z.string().regex(/^[a-f\d]{24}$/i);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(s => {
    const d = new Date(s);
    return Number.isFinite(+d) && d.toISOString().slice(0, 10) === s;
}, 'Use a valid date.');
export const PoAddressSchema = z.object({
    name: text(160).default(''), line1: text(200).default(''), line2: text(200).default(''),
    city: text(100).default(''), state: text(100).default(''), postal_code: text(30).default(''),
    country: text(2).default('US'),
}).strict();
export const PoConfigurationSchema = z.object({
    key: z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/), model: text(160).min(1),
    part_number: text(100).default(''), quantity: z.number().int().min(1).max(10000),
    branding: z.boolean(), connectivity: z.enum(['wifi', 'evi_cellular']).optional(),
    firmware: text(100).default(''),
    unit_price_cents: z.number().int().min(0).max(100000000).optional(),
    charger_type: text(100).default(''), amperage: z.number().positive().max(1000).optional(),
    connector_types: z.array(z.enum(['J1772', 'CCS2', 'CCS1', 'CHAdeMO', 'Type2', 'NACS'])).min(1).max(8).default(['J1772']),
    max_power_kw: z.number().positive().max(2000).optional(),
    pos: z.boolean().default(false), assistance_phone: text(50).default(''),
    screensaver: z.boolean().default(false), sim_cards: z.literal('customer_provides').default('customer_provides'),
}).strict();
export const DeploymentPlanSchema = z.object({
    key: z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/),
    site_name: text(160).min(1), address: PoAddressSchema,
    location_id: id.optional(),
    customer_name: text(160).min(1), customer_email: z.string().email().max(254),
    origination: z.enum(['partner_originated', 'evi_sourced']),
    /** A Sales Partner New job this plan installs (PartnerJob): approval creates that job's deployment. */
    job_id: id.optional(),
    configurations: z.array(z.object({ configuration_key: z.string().min(1).max(40),
        quantity: z.number().int().min(1).max(10000) }).strict()).min(1).max(50),
}).strict();
export const PoContentSchema = z.object({
    customer_order_ids: z.array(id).max(50).default([]),
    ocpp_base_url: z.literal(PO_OCPP_BASE_URL).default(PO_OCPP_BASE_URL),
    currency: z.enum(['USD', 'CAD']).default('USD'),
    freight_quote_cents: z.number().int().min(0).max(100000000).optional(),
    invoice_reference: text(160).default(''),
    contacts: z.array(z.object({ name: text(120).min(1), purpose: text(120).min(1), email: z.union([z.string().email(), z.literal('')]) }).strict()).max(20).default(PO_CONTACTS.map(c => ({ ...c }))),
    configurations: z.array(PoConfigurationSchema).max(100).default([]),
    ship_to: PoAddressSchema.optional(),
    desired_ship_date: date.optional(),
    /** Autel's confirmed ready date, recorded with "Autel confirmed" (Warren, 21 October 2026): it replaces the computed
     *  ship date for this PO's jobs (lib/expectedDates.ts in @evi/shared). */
    ready_date: date.optional(),
    contact_name: text(120).default('Warren'),
    contact_email: z.string().email().max(254).default('warren@evinitiative.com'),
    notes: text(4000).default(''),
    deployment_plans: z.array(DeploymentPlanSchema).max(50).default([]),
    /** Home drop-ship units on this PO (New job spec 5.3, approved 20 October 2026): shipped to the homeowner, with no
     *  reserved identity, no plan and no deployment, so My Home registration keeps working. */
    home_lines: z.array(z.object({ order_id: id, job_id: id, model: text(160).min(1), part_number: text(100).default(''),
        quantity: z.number().int().min(1).max(1000), ship_to: PoAddressSchema }).strict()).max(50).default([]),
}).strict().superRefine((v, ctx) => {
    const keys = v.configurations.map(c => c.key);
    if (v.configurations.reduce((n, g) => n + g.quantity, 0) > 500)
        ctx.addIssue({ code: 'custom', message: 'Limit a PO to 500 physical units. Split larger supplier orders.' });
    if (new Set(keys).size !== keys.length)
        ctx.addIssue({ code: 'custom', message: 'Configuration keys must be unique.' });
    if (new Set(v.customer_order_ids).size !== v.customer_order_ids.length)
        ctx.addIssue({ code: 'custom', message: 'Order links must be unique.' });
    if (new Set(v.deployment_plans.map(p => p.key)).size !== v.deployment_plans.length)
        ctx.addIssue({ code: 'custom', message: 'Plan keys must be unique.' });
    const totals = new Map();
    for (const plan of v.deployment_plans)
        for (const allocation of plan.configurations) {
            totals.set(allocation.configuration_key, (totals.get(allocation.configuration_key) ?? 0) + allocation.quantity);
        }
    for (const [key, count] of totals) {
        const configuration = v.configurations.find(c => c.key === key);
        if (!configuration || count > configuration.quantity)
            ctx.addIssue({ code: 'custom',
                message: 'Plan allocations must reference a configuration and cannot exceed its quantity.' });
    }
});
export const PoDraftSchema = z.object({
    idempotency_id: z.string().min(8).max(100).regex(/^[a-zA-Z0-9_-]+$/),
    content: PoContentSchema,
}).strict();
export const PoEditSchema = z.object({ version: z.number().int().min(0), content: PoContentSchema }).strict();
export const PoTransitionSchema = z.object({
    version: z.number().int().min(0), stage: z.enum(PO_STAGES), occurred_at: z.string().datetime(),
    note: text(1000).default(''), tracking_number: text(120).optional(), tracking_carrier: text(120).optional(),
    freight_quote_cents: z.number().int().min(0).max(100000000).optional(), invoice_reference: text(160).optional(),
    /** With po_acknowledged only: the ready date Autel confirmed, when it gave one. */
    ready_date: date.optional(),
}).strict();
export function poApprovalBlockers(content) {
    const blockers = [];
    if (!content.configurations.length)
        blockers.push('Add at least one hardware configuration.');
    if (content.configurations.some(c => !c.connectivity))
        blockers.push('Choose connectivity for every configuration.');
    if (content.configurations.some(c => c.unit_price_cents === undefined || !c.part_number))
        blockers.push('Set the supplier price and part number for every configuration.');
    const address = content.ship_to;
    if (!address || ![address.name, address.line1, address.city, address.state, address.postal_code, address.country].every(Boolean))
        blockers.push('Complete the ship-to address.');
    if (!content.desired_ship_date)
        blockers.push('Set the desired ship date.');
    if (!content.contact_name)
        blockers.push('Set the contact name.');
    return blockers;
}
export function poStalled(po, now = new Date()) {
    const result = [], age = (when) => (+now - +new Date(when)) / 86400000;
    const since = (stage) => po.history.find(e => e.stage === stage)?.occurred_at;
    if (!po.approval && age(po.created_at) > 2)
        result.push('Approval missing for more than 2 days.');
    if (po.stage === 'po_sent' && since('po_sent') && age(since('po_sent')) > 3)
        result.push('No acknowledgment for more than 3 days.');
    if (!po.manifest.length && since('po_acknowledged') && age(since('po_acknowledged')) > 10)
        result.push('No serials for more than 10 days.');
    if (!['delivered', 'pushed_online'].includes(po.stage) && !po.tracking_number && po.content.desired_ship_date
        && po.content.desired_ship_date < now.toISOString().slice(0, 10))
        result.push('No tracking after the desired ship date.');
    return result;
}
export const PO_EMAIL_TYPES = ['quote_request', 'purchase_order', 'precommission', 'sim_shipment'];
/** What a PO whose freight Warp carries tells Autel (Warren, 21 October 2026, evening): EVI books the pickup with its
 * carrier, Autel must not arrange shipping, and the ready date is what EVI needs. The booking email follows with the
 * carrier, the pickup window and the bill of lading. */
export const PO_WARP_PICKUP_LINE = 'EV Initiative books the pickup with its own carrier: please do not arrange shipping for this order. Please confirm the date the order will be ready for pickup; once it is booked, we email you the carrier, the pickup window and the bill of lading.';
export function poEmailPreview(po, type = 'purchase_order') {
    const c = po.content, ship = c.ship_to;
    // Warp carries the freight (Warren, 21 October 2026): Autel quotes the hardware only, and EVI books the pickup.
    const warp = po.freight_by?.carrier === 'warp';
    const rows = c.configurations.map(g => [g.key, g.model, g.part_number || 'Not specified', String(g.quantity),
        g.branding ? 'Yes' : 'No', g.connectivity === 'wifi' ? 'WiFi' : g.connectivity === 'evi_cellular' ? 'EVI cellular' : 'To confirm',
        g.firmware || 'Not specified'].join(' | '));
    const body = [
        'Hello Autel Order Team,', '', 'Please review purchase order ' + po.po_number + ' for EV Initiative.',
        'Please confirm availability, pricing, pre-commissioning arrangements and the shipping schedule.',
        // Warren, 21 October 2026, evening: "The PO to Autel says EVI books the pickup and Autel must not arrange shipping."
        ...(warp ? [PO_WARP_PICKUP_LINE] : []),
        '', 'Configuration | Model | Part number | Quantity | Branding | Connectivity | Firmware',
        ...rows,
        ...(c.home_lines?.length ? ['', 'Drop-ship to homeowners (not pre-commissioned to an EVI station ID; the homeowner registers each unit):',
            ...c.home_lines.map(h => h.quantity + ' x ' + h.model + (h.part_number ? ' (' + h.part_number + ')' : '') + ' to '
                + [h.ship_to.name, h.ship_to.line1, h.ship_to.city + ', ' + h.ship_to.state + ' ' + h.ship_to.postal_code, h.ship_to.country].filter(Boolean).join(', '))] : []),
        '', 'Ship to:', ...(ship ? [ship.name, ship.line1, ship.line2, ship.city + ', ' + ship.state + ' ' + ship.postal_code, ship.country].filter(Boolean) : ['To confirm']),
        '', 'Desired ship date: ' + (c.desired_ship_date || 'To confirm'),
        '', 'Pre-commissioning form: https://autelenergy.us/pages/ev-charger-pre-commissioning',
        'One form is required per hardware configuration, with the PO available.',
        'For branded units, branding arrangements must be completed at least two weeks before the desired ship date.',
        'Please provide unit serial numbers and the configuration mapping when available.',
        'Charger credentials will be accessed only through an authorized signed-in EVI screen; they are not included in this email.',
        '', 'Contact: ' + c.contact_name + ' <' + c.contact_email + '>', 'EV Initiative',
    ].join('\n');
    const subjects = { quote_request: 'Freight and hardware quote request', purchase_order: 'Purchase order', precommission: 'Pre-commissioning answers', sim_shipment: 'SIM shipment notice' };
    const subject = 'EV Initiative ' + po.po_number + ': ' + subjects[type];
    const to = 'evorder@autel.com';
    const cc = type === 'quote_request' ? 'mwan@autel.com, Logistics.NC@autel.com' : 'mwan@autel.com';
    const prefix = type === 'quote_request' ? (warp ? 'Please quote the hardware below. EV Initiative books the freight with Warp, so no freight quote is needed. This is a quote request, not a purchase authorization.\n\n'
        : 'Please quote the hardware and freight below. This is a quote request, not a purchase authorization.\n\n')
        : type === 'sim_shipment' ? 'Customer-provided SIM cards are being sent for ' + po.po_number + '.\nDestination: Autel, 8420 Triad Drive, Greensboro, Attn Matthew Buchanan.\nPlease confirm receipt and the unit-to-SIM mapping. Shipment details: ' + (c.notes || 'To be supplied by Warren before sending.') + '\n\n'
            : type === 'precommission' ? poPrecommissionSheets(po).map(s => s.text).join('\n\n') + '\n\n' : '';
    const finalBody = prefix + body + '\n\nSupplier prices (' + c.currency + '):\n'
        + c.configurations.map(g => g.part_number + ' x ' + g.quantity + ': ' + (g.unit_price_cents === undefined ? 'To confirm' : (g.unit_price_cents / 100).toFixed(2)) + ' per unit').join('\n')
        + '\nFreight: ' + (warp ? 'Booked by EV Initiative with Warp for pickup on your ready date' : c.freight_quote_cents === undefined ? 'To confirm' : (c.freight_quote_cents / 100).toFixed(2));
    return { to, cc, subject, body: finalBody, text: 'To: ' + to + '\nCc: ' + cc + '\nSubject: ' + subject + '\n\n' + finalBody };
}
/** TSV from a spreadsheet or CSV. Never echoes input values in validation errors. */
export function parsePoManifest(input, poNumber, configurations) {
    const errors = [], records = [];
    if (input.length > 500000)
        return { rows: [], pins: [], errors: ['Manifest is too large.'], ready: false };
    const separator = input.includes('\t') ? '\t' : ',';
    let row = [], cell = '', quoted = false;
    for (let i = 0; i <= input.length; i++) {
        const ch = input[i];
        if (ch === '"') {
            if (quoted && input[i + 1] === '"') {
                cell += '"';
                i++;
            }
            else
                quoted = !quoted;
        }
        else if (!quoted && (ch === separator || ch === '\n' || ch === undefined)) {
            row.push(cell.trim());
            cell = '';
            if (ch !== separator) {
                if (row.some(Boolean))
                    records.push(row);
                row = [];
            }
        }
        else if (ch !== '\r' && ch !== undefined)
            cell += ch;
    }
    if (quoted)
        errors.push('Unclosed CSV quote.');
    const headers = records.shift()?.map(h => h.toLowerCase().replace(/[^a-z0-9]/g, '')) ?? [];
    const required = ['delivery', 'po', 'carrier', 'tracking', 'product', 'sn', 'pin', 'sim', 'stationid'];
    if (required.some(h => !headers.includes(h)) || new Set(headers).size !== headers.length)
        return { rows: [], pins: [], errors: ['Use columns: Delivery, PO, Carrier, Tracking, Product, S/N, PIN, SIM, Station ID.'], ready: false };
    const rows = [], pins = [], serials = new Set(), stations = new Set();
    for (const [i, values] of records.entries()) {
        const get = (key) => values[headers.indexOf(key)] ?? '';
        const product = get('product'), serial = get('sn'), station = get('stationid') || serial;
        const matching = configurations.filter(g => [g.key, g.part_number, g.model].some(v => v && v.toLowerCase() === product.toLowerCase()));
        if (values.length !== headers.length || values.some(v => v.length > 250))
            errors.push('Row ' + (i + 2) + ': invalid column count or length.');
        if (get('po').replace(/[^0-9]/g, '') !== poNumber.replace(/[^0-9]/g, ''))
            errors.push('Row ' + (i + 2) + ': PO does not match.');
        if (matching.length !== 1)
            errors.push('Row ' + (i + 2) + ': product must match exactly one configuration. Use its configuration key if ambiguous.');
        if (!/^[A-Za-z0-9_-]{1,100}$/.test(serial) || !/^[A-Za-z0-9_-]{1,100}$/.test(station))
            errors.push('Row ' + (i + 2) + ': invalid serial or station ID.');
        if (serials.has(serial) || stations.has(station))
            errors.push('Row ' + (i + 2) + ': duplicate serial or station ID.');
        if (!get('carrier') || !get('tracking'))
            errors.push('Row ' + (i + 2) + ': carrier and tracking required.');
        serials.add(serial);
        stations.add(station);
        pins.push(get('pin'));
        rows.push({ delivery: get('delivery'), po: poNumber, carrier: get('carrier'), tracking: get('tracking'), product,
            serial, station_id: station, sim: get('sim'), configuration_key: matching[0]?.key ?? '', pin_present: !!get('pin') });
    }
    for (const group of configurations)
        if (rows.filter(r => r.configuration_key === group.key).length !== group.quantity)
            errors.push('Configuration ' + group.key + ': manifest quantity must equal ' + group.quantity + '.');
    if (!rows.length)
        errors.push('Add at least one unit.');
    return { rows, pins, errors, ready: !errors.length };
}
/** One screen QR per connector; an ID the driver app cannot open is held back rather than printed. */
function unitQrs(site, identity, ports) {
    try {
        return Array.from({ length: ports }, (_, i) => 'connector ' + (i + 1) + ' ' + chargerQrUrl(site, identity, i + 1)).join(', ');
    }
    catch {
        return 'Check the station ID and location before printing. Do not print.';
    }
}
export function poPrecommissionSheets(po) {
    return po.content.configurations.map(g => {
        const reserved = po.factory_units?.filter(r => r.configuration_key === g.key);
        const units = reserved?.length ? reserved.map(r => ({ serial: r.serial ?? 'Awaiting serial', station_id: r.identity, configuration_key: r.configuration_key, location_verified: r.location_verified, plan_key: r.plan_key }))
            : po.manifest.filter(r => r.configuration_key === g.key).map(r => ({ ...r, location_verified: true, plan_key: undefined }));
        const plans = po.content.deployment_plans.filter(p => p.configurations.some(a => a.configuration_key === g.key));
        // Ambiguous multi-location allocations must be resolved by a human, not guessed.
        const onlyPlan = plans.length === 1 ? plans[0] : undefined;
        const allocated = onlyPlan?.configurations.filter(a => a.configuration_key === g.key).reduce((n, a) => n + a.quantity, 0);
        const location = allocated === g.quantity ? onlyPlan?.location_id : undefined;
        const text = [po.po_number + ' / Configuration ' + g.key, 'Charger type: ' + (g.charger_type || 'To confirm'),
            'Model: ' + g.model, 'Quantity: ' + g.quantity, 'Amperage: ' + (g.amperage ?? 'To confirm'),
            'POS: ' + (g.pos ? 'Yes' : 'No'), 'Customer assistance phone: ' + (g.assistance_phone || 'None'),
            'Screensaver: ' + (g.screensaver ? 'Yes' : 'No'), 'Branding decals: ' + (g.branding ? 'Yes' : 'No'),
            'SIM cards: Customer provides', 'Connectivity: ' + (g.connectivity === 'wifi' ? 'WiFi' : g.connectivity === 'evi_cellular' ? 'EVI cellular' : 'To confirm'),
            ...(g.connectivity === 'wifi' ? ['WiFi network: the installer joins the site network on install day. Network passwords never go on this sheet.'] : []),
            'Ports per unit: ' + g.connector_types.length + ' (' + g.connector_types.join(', ') + ')',
            'Firmware: ' + (g.firmware || 'To confirm'), 'OCPP base URL: ' + po.content.ocpp_base_url,
            'Subprotocol: ocpp1.6. Station ID is the reserved EVI identity and never changes when a serial arrives.',
            ...(po.preflip_note ? [po.preflip_note] : []),
            'QR instructions: print one unique URL per connector, each on the screen for that connector; do not print a shared location-only QR.',
            // Per unit (Warren, 21 October 2026): EVI's charger ID, model, ports, endpoint and one screen QR per connector.
            // A per-charger credential is never on this sheet; Autel's pre-commissioning contact reads it on the signed-in screen.
            ...(units.length ? units.map(u => 'Serial: ' + u.serial + ' | Station ID: ' + u.station_id + ' | Model: ' + g.model + ' | Ports: ' + g.connector_types.length
                + ' | OCPP: ' + po.content.ocpp_base_url + encodeURIComponent(u.station_id)
                + ' | QR: ' + (() => {
                const site = u.plan_key ? plans.find(p => p.key === u.plan_key)?.location_id : location;
                return site && u.location_verified
                    ? unitQrs(site, u.station_id, g.connector_types.length) : 'Awaiting verified location mapping. Do not print.';
            })())
                : ['Unit serials and per-unit QR URLs: awaiting manifest. Do not print placeholder QRs.']),
            'Pre-commissioning form: https://autelenergy.us/pages/ev-charger-pre-commissioning',
        ].join('\n');
        return { configuration_key: g.key, text, fields: {
                'PO number': po.po_number, 'Charger type': g.charger_type || 'To confirm', 'Model': g.model, 'Amperage': String(g.amperage ?? 'To confirm'),
                'POS': g.pos ? 'Yes' : 'No', 'QR instructions': text.split('\n').filter(s => s.startsWith('QR instructions:') || s.startsWith('Serial:')).join('\n'),
                'Station IDs': units.map(u => u.station_id).join('\n'), 'Customer assistance phone': g.assistance_phone || 'None',
                'Screensaver': g.screensaver ? 'Yes' : 'No', 'Branding decals': g.branding ? 'Yes' : 'No', 'SIM cards': 'Customer provides', 'OCPP URL': po.content.ocpp_base_url,
                'Ports per unit': String(g.connector_types.length),
            } };
    });
}
//# sourceMappingURL=procurement.js.map